import React from "react"
import { DatePicker } from "antd"
import dayjs from "dayjs"
import { SearchOutlined, CalendarOutlined } from "@ant-design/icons"
import {
  FilterOperator,
  type QueryParamsDto,
  type QueryParamsFilter,
  type SortDirection,
} from "@mimber/schema"
import type { ColumnsType } from "../components"

/**
 * Shared table utility functions for Mimber applications
 */

interface TableChangeParams {
  pagination: {
    current?: number
    pageSize?: number
  }
  filters: Record<string, string[] | null>
  sorter: {
    field?: string | string[]
    order?: "ascend" | "descend"
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColumnFieldKey = (column: any) => {
  if (column.key !== undefined && column.key !== null && column.key !== "") {
    return String(column.key)
  }

  if (Array.isArray(column.dataIndex)) {
    return column.dataIndex.map(String).join(".")
  }

  if (column.dataIndex !== undefined && column.dataIndex !== null) {
    return String(column.dataIndex)
  }

  return undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColumnSortCandidates = (column: any): string[] => {
  const candidates = new Set<string>()

  if (Array.isArray(column.dataIndex)) {
    candidates.add(column.dataIndex.map(String).join("."))
  } else if (column.dataIndex !== undefined && column.dataIndex !== null) {
    candidates.add(String(column.dataIndex))
  }

  if (column.key !== undefined && column.key !== null && column.key !== "") {
    candidates.add(String(column.key))
  }

  return Array.from(candidates)
}

const getFilteredValueFromQueryFilter = (
  queryFilter: unknown
): React.Key[] | null => {
  if (
    !queryFilter ||
    typeof queryFilter !== "object" ||
    Array.isArray(queryFilter)
  ) {
    return null
  }

  const filter = queryFilter as Record<string, unknown>

  if (typeof filter[FilterOperator.CONTAINS] === "string") {
    return [String(filter[FilterOperator.CONTAINS])]
  }

  if (typeof filter[FilterOperator.EQUALS] === "string") {
    return [String(filter[FilterOperator.EQUALS])]
  }

  if (Array.isArray(filter[FilterOperator.IN])) {
    return (filter[FilterOperator.IN] as unknown[]).map(String)
  }

  if (Array.isArray(filter[FilterOperator.BETWEEN])) {
    const betweenValues = filter[FilterOperator.BETWEEN] as unknown[]
    if (betweenValues.length === 2) {
      return betweenValues.map(String)
    }
  }

  return null
}

/**
 * Applies QueryParams sorting/filter state back onto columns so antd
 * controlled UI indicators (sort arrows, filter icon highlights) match
 * remembered table state on initial render.
 */
export const applyQueryParamsToColumns = <T extends unknown>(
  columns: ColumnsType<T>,
  queryParams: QueryParamsDto
): ColumnsType<T> => {
  const sortEntry = queryParams.sort
    ? Object.entries(queryParams.sort)[0]
    : null
  const activeSortField = sortEntry?.[0]
  const activeSortOrder =
    sortEntry?.[1] === "asc"
      ? "ascend"
      : sortEntry?.[1] === "desc"
        ? "descend"
        : undefined

  return columns.map((column) => {
    const fieldKey = getColumnFieldKey(column)
    const sortCandidates = getColumnSortCandidates(column)
    const hasFilterConfig =
      ("filters" in column && Boolean(column.filters)) ||
      ("filterDropdown" in column && Boolean(column.filterDropdown)) ||
      ("filterType" in column && column.filterType === "dateRange")

    const nextColumn = {
      ...column,
    } as typeof column & {
      sortOrder?: "ascend" | "descend" | null
      filteredValue?: React.Key[] | null
    }

    if (activeSortField && "sorter" in column && column.sorter) {
      const isSortMatch = sortCandidates.some(
        (candidate) =>
          candidate === activeSortField ||
          activeSortField.endsWith(`.${candidate}`)
      )
      nextColumn.sortOrder = isSortMatch ? activeSortOrder : null
    }

    if (fieldKey && hasFilterConfig) {
      const queryFilter = queryParams.filters?.[fieldKey]
      nextColumn.filteredValue = getFilteredValueFromQueryFilter(queryFilter)
    }

    return nextColumn
  })
}

/**
 * Handles table change events (pagination, filtering, sorting) and converts them to QueryParams
 * Automatically detects filter types based on column configuration:
 * - Columns with filterDropdown → column-specific search (CONTAINS operator)
 * - Columns with filters array → exact match (EQUALS operator)
 * Note: For global search across all searchable fields, use a separate search input with the search parameter
 */
export const handleTableChange = (
  tableChangeData: TableChangeParams,
  updateFunction: Function,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns?: ColumnsType<any>
) => {
  const { pagination, filters, sorter } = tableChangeData

  const queryParams: QueryParamsDto = {
    page: pagination.current || 1,
    limit: pagination.pageSize || 10,
  }

  // Handle sorting
  if (sorter && sorter.field) {
    const field = Array.isArray(sorter.field)
      ? sorter.field.join(".")
      : String(sorter.field)
    const order = (sorter.order === "ascend" ? "asc" : "desc") as SortDirection
    queryParams.sort = { [field]: order }
  }

  // Build a map of filter keys to their types by inspecting columns
  const filterTypeMap: Record<string, "contains" | "exact" | "dateRange"> = {}
  if (columns) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns.forEach((col: any) => {
      const key = col.key as string
      if (!key) return

      // Columns tagged with filterType: "dateRange" (from getColumnDateRangeProps)
      if (col.filterType === "dateRange") {
        filterTypeMap[key] = "dateRange"
      }
      // Columns with filterDropdown are column-specific search filters (CONTAINS)
      else if ("filterDropdown" in col && col.filterDropdown) {
        filterTypeMap[key] = "contains"
      }
      // Columns with filters array are exact match filters (EQUALS)
      else if ("filters" in col && col.filters) {
        filterTypeMap[key] = "exact"
      }
    })
  }

  // Handle filters
  const filterObj: QueryParamsFilter = {}

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value.length === 0) return

      const filterType = filterTypeMap[key]

      if (filterType === "dateRange") {
        // Date range filters use BETWEEN operator with [startISO, endISO]
        if (value.length === 2) {
          filterObj[key] = {
            [FilterOperator.BETWEEN]: [String(value[0]), String(value[1])],
          }
        }
      } else if (filterType === "contains") {
        // Column-specific search filters use CONTAINS operator
        filterObj[key] = { [FilterOperator.CONTAINS]: String(value[0]) }
      } else if (filterType === "exact") {
        if (value.length === 1) {
          filterObj[key] = { [FilterOperator.EQUALS]: String(value[0]) }
        } else {
          filterObj[key] = { [FilterOperator.IN]: value.map(String) }
        }
      }
    })
  }

  if (Object.keys(filterObj).length > 0) {
    queryParams.filters = filterObj
  }

  updateFunction(queryParams)
}

/**
 * Creates standard pagination configuration for tables
 */
export const createPaginationConfig = (
  queryParams: QueryParamsDto,
  total: number = 0,
  entityName: string = "items"
) => ({
  current: queryParams.page || 1,
  pageSize: queryParams.limit || 10,
  total,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number, range: [number, number]) =>
    `${range[0]}-${range[1]} of ${total} ${entityName}`,
  pageSizeOptions: ["10", "20", "50", "100"],
})

/**
 * Common status filters for active/inactive entities
 */
export const createStatusFilters = () => [
  { text: "Active", value: "active" },
  { text: "Inactive", value: "inactive" },
]

/**
 * Creates standard row click handler configuration
 */
export const createRowClickHandler =
  <T extends unknown>(onRowClick?: Function) =>
  (record: T) => {
    return {
      onClick: () => onRowClick?.(record),
      style: { cursor: onRowClick ? "pointer" : "default" },
    }
  }

/**
 * Format date for table display
 */
export const formatTableDate = (dateString: string | null) => {
  if (!dateString) return "Never"
  return new Date(dateString).toLocaleString()
}

/**
 * Format a datetime for table display (alias for formatTableDate)
 */
export const formatTableDateTime = formatTableDate

/**
 * Truncate text for table display
 */
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return ""
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

/**
 * Compatibility alias for createPaginationConfig
 * @deprecated Use createPaginationConfig instead
 */
export const getPaginationConfig = (
  total: number,
  queryParams: QueryParamsDto,
  entityName: string = "items"
) => createPaginationConfig(queryParams, total, entityName)

/**
 * Compatibility alias for createSearchFilterDropdown
 * @deprecated Use createSearchFilterDropdown instead
 */
export const getColumnSearchProps = (placeholder: string = "Search") => {
  const dropdown = createSearchFilterDropdown(placeholder)
  return {
    filterDropdown: dropdown,
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
  }
}

/**
 * Creates a date range filter dropdown for table columns.
 * Tags the column with filterType: "dateRange" so handleTableChange emits
 * a BETWEEN filter with [startISO, endISO].
 */
export const getColumnDateRangeProps = (_label: string = "Date") => {
  return {
    filterType: "dateRange" as const,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <DatePicker.RangePicker
          style={{ marginBottom: 8, display: "block" }}
          value={[
            selectedKeys[0] ? dayjs(String(selectedKeys[0])) : null,
            selectedKeys[1] ? dayjs(String(selectedKeys[1])) : null,
          ]}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setSelectedKeys([
                dates[0].startOf("day").toISOString(),
                dates[1].endOf("day").toISOString(),
              ])
            } else {
              setSelectedKeys([])
            }
          }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => confirm()}
            style={{
              padding: "4px 8px",
              fontSize: 12,
              backgroundColor: "#1890ff",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              flex: 1,
            }}
          >
            Filter
          </button>
          <button
            type="button"
            onClick={() => {
              clearFilters?.()
              confirm()
            }}
            style={{
              padding: "4px 8px",
              fontSize: 12,
              backgroundColor: "#f5f5f5",
              color: "#333",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              cursor: "pointer",
              flex: 1,
            }}
          >
            Reset
          </button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
  }
}

/**
 * Interface for filter dropdown props to maintain type safety
 */
export interface FilterDropdownProps {
  setSelectedKeys: (keys: React.Key[]) => void
  selectedKeys: React.Key[]
  confirm: () => void
  clearFilters?: () => void
}

/**
 * Creates a reusable search filter dropdown for table columns
 */
export const createSearchFilterDropdown = (
  placeholder: string = "Search",
  searchButtonText: string = "Search",
  resetButtonText: string = "Reset"
) => {
  return ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => {
    // Note: We avoid importing antd types directly to maintain clean separation through ui-web package

    return (
      <div style={{ padding: 8 }}>
        <input
          placeholder={placeholder}
          value={selectedKeys[0] ? String(selectedKeys[0]) : ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              confirm()
            }
          }}
          style={{
            marginBottom: 8,
            display: "block",
            width: "100%",
            padding: "4px 8px",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
          }}
        />
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            onClick={() => confirm()}
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              backgroundColor: "#1890ff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              width: "90px",
            }}
          >
            {searchButtonText}
          </button>
          <button
            type="button"
            onClick={() => clearFilters?.()}
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              backgroundColor: "#f5f5f5",
              color: "#333",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              cursor: "pointer",
              width: "90px",
            }}
          >
            {resetButtonText}
          </button>
        </div>
      </div>
    )
  }
}
